import { createContext, useContext, useEffect, useCallback, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import tokenStore from '../services/tokenStore';
import autoSync from '../services/AutoSync';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DataContext = createContext();

/**
 * Build axios config with the in-memory access token and (for redundancy)
 * the httpOnly refresh cookie. Returns `{}` when no token is available
 * yet so requests will surface 401 instead of silently appearing unauth'd.
 */
function authConfig() {
  const token = tokenStore.getAccessToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
}

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};

const INITIAL_DATA = {
  employees: [
    {
      id: '1',
      name: 'Admin User',
      email: 'admin',
      phone: '555-0001',
      role: 'admin',
      hourlyRate: 25,
      username: 'admin',
      password: 'admin123',
      status: 'active',
      createdAt: new Date().toISOString()
    },
    {
      id: '2',
      name: 'Manager User',
      email: 'manager',
      phone: '555-0002',
      role: 'manager',
      hourlyRate: 22,
      username: 'manager',
      password: 'manager123',
      status: 'active',
      createdAt: new Date().toISOString()
    },
    {
      id: '3',
      name: 'Employee User',
      email: 'employee@maidly.com',
      phone: '555-0003',
      role: 'employee',
      hourlyRate: 18,
      username: 'employee',
      password: 'emp123',
      status: 'active',
      createdAt: new Date().toISOString()
    }
  ],
  customers: [],
  timeEntries: [],
  quotes: [],
  invoices: [],
  quoteTemplates: [],
  invoiceTemplates: [],
  mileageEntries: [],
  appointments: [],
  inventory: [],
  expenses: [],
  settings: {
    mileageRate: 0.655,
    salesTaxRate: 8
  },
  signatures: []
};

// Data types that should be synced
const SYNC_DATA_TYPES = [
  'employees', 'customers', 'invoices', 'quotes', 'appointments',
  'inventory', 'expenses', 'mileageEntries', 'timeEntries', 'settings'
];

export const DataProvider = ({ children }) => {
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, synced, error, offline
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const syncTimeoutRef = useRef(null);
  const pendingChangesRef = useRef(new Set());

  // Get current user from localStorage. Supports BOTH auth stores:
  //   - maidly_auth_user  (SecureAuthContext / registration flow)
  //   - maidly_current_user (AuthContext / login flow)
  // so the Storage page's manual sync buttons work regardless of how the
  // user authenticated.
  const getCurrentUser = useCallback(() => {
    const userStr =
      localStorage.getItem('maidly_auth_user') ||
      localStorage.getItem('maidly_current_user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }, []);

  // The cloud sync key mirrors SyncService.getSyncKey(): the active team
  // workspace wins, falling back to the personal user id for solo accounts.
  // This keeps the Storage page's manual Sync/Load buttons consistent with the
  // app-wide AutoSync path (which is workspace-scoped).
  const getSyncKey = useCallback(() => {
    const ws = localStorage.getItem('maidly_workspace_id');
    if (ws) return ws;
    const user = getCurrentUser();
    return user?.id || null;
  }, [getCurrentUser]);

  // Get device ID
  const getDeviceId = useCallback(() => {
    let deviceId = localStorage.getItem('maidly_device_id');
    if (!deviceId) {
      deviceId = 'device-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
      localStorage.setItem('maidly_device_id', deviceId);
    }
    return deviceId;
  }, []);

  // Initialize local storage with default data
  useEffect(() => {
    Object.keys(INITIAL_DATA).forEach(key => {
      if (!localStorage.getItem(`maidly_${key}`)) {
        localStorage.setItem(`maidly_${key}`, JSON.stringify(INITIAL_DATA[key]));
      }
    });
  }, []);

  // Online/offline listeners — state only. The actual cloud pull/push and
  // realtime wiring live in Layout + AutoSync + RealtimeService (workspace-
  // aware, field-stamped, delete-correct). DataContext intentionally no longer
  // runs its own divergent mount/online/login sync, which previously did an
  // additive-only merge (never reflected remote updates/deletes) and echoed
  // writes back to the cloud.
  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); setSyncStatus('idle'); };
    const handleOffline = () => { setIsOnline(false); setSyncStatus('offline'); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync a single data type (debounced)
  const syncDataType = useCallback((dataType, data) => {
    const key = getSyncKey();
    if (!key || !isOnline || !syncEnabled) return;

    // Mark as pending
    pendingChangesRef.current.add(dataType);
    setSyncStatus('syncing');

    // Debounce - wait 2 seconds before syncing
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(async () => {
      try {
        const pendingTypes = Array.from(pendingChangesRef.current);
        const dataToSync = {};
        
        pendingTypes.forEach(type => {
          dataToSync[type] = loadDataLocal(type);
        });

        await axios.post(`${API_URL}/api/sync/bulk-save`, {
          user_id: key,
          device_id: getDeviceId(),
          data: dataToSync
        }, authConfig());

        setLastSyncTime(new Date());
        setSyncStatus('synced');
        pendingChangesRef.current.clear();
      } catch (error) {
        console.error('Sync error:', error);
        setSyncStatus('error');
      }
    }, 2000);
  }, [isOnline, syncEnabled, getSyncKey, getDeviceId]);

  // Load data from local storage (internal)
  const loadDataLocal = (dataType) => {
    const data = localStorage.getItem(`maidly_${dataType}`);
    return data ? JSON.parse(data) : INITIAL_DATA[dataType] || [];
  };

  // Save data to local storage AND sync to cloud
  const saveData = useCallback((dataType, data) => {
    // Save locally immediately
    localStorage.setItem(`maidly_${dataType}`, JSON.stringify(data));
    
    // Trigger cloud sync (debounced)
    if (SYNC_DATA_TYPES.includes(dataType)) {
      syncDataType(dataType, data);
    }
  }, [syncDataType]);

  // Load data from local storage
  const loadData = useCallback((dataType) => {
    return loadDataLocal(dataType);
  }, []);

  // Force sync now — delegates to the app-wide AutoSync path so the Storage
  // page pushes the SAME (workspace-aware, field-stamped) payload the rest of
  // the app uses. Avoids the old divergent per-user merge path.
  const forceSync = useCallback(async () => {
    if (!getSyncKey()) {
      toast.error('Please log in to sync data');
      return;
    }
    try {
      setSyncStatus('syncing');
      await autoSync.pushAll();
      setLastSyncTime(new Date());
      setSyncStatus('synced');
      toast.success('Data synced to cloud');
    } catch (e) {
      setSyncStatus('error');
      toast.error('Sync failed — will retry automatically');
    }
  }, [getSyncKey]);

  // Force load from cloud — full workspace pull (reflects remote updates AND
  // deletes), then notify pages to re-read their slice.
  const forceLoadFromCloud = useCallback(async () => {
    if (!getSyncKey()) {
      toast.error('Please log in to load data');
      return;
    }
    try {
      setSyncStatus('syncing');
      await autoSync.pullFromCloud();
      setLastSyncTime(new Date());
      setSyncStatus('synced');
      window.dispatchEvent(new CustomEvent('maidly:cloud-data-updated', { detail: { source: 'manual' } }));
      toast.success('Data loaded from cloud');
    } catch (e) {
      setSyncStatus('error');
      toast.error('Load failed — using local data');
    }
  }, [getSyncKey]);

  // Toggle sync
  const toggleSync = useCallback((enabled) => {
    setSyncEnabled(enabled);
    localStorage.setItem('maidly_sync_enabled', JSON.stringify(enabled));
    if (enabled) {
      toast.success('Auto-sync enabled');
      if (getSyncKey()) autoSync.pushAll().catch(() => {});
    } else {
      toast.info('Auto-sync disabled');
    }
  }, [getSyncKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return (
    <DataContext.Provider value={{ 
      saveData, 
      loadData,
      syncStatus,
      lastSyncTime,
      isOnline,
      syncEnabled,
      forceSync,
      forceLoadFromCloud,
      toggleSync
    }}>
      {children}
    </DataContext.Provider>
  );
};
